// FILE: src/components/ReadinessPage.js
//
// Phase E2 redesign — the Readiness page as a premium coaching experience,
// matching the Dashboard / Study Plan visual language. Layout + data wiring
// live here; sections live in ReadinessSections.js (hero, coaching card,
// metric grid, opportunity panel) and ReadinessExtras.js (history chart,
// milestones, contribution table, banner). Pure copy/derivation helpers are
// in readinessPageHelpers.js.
//
// All readiness calculations are untouched — this page only re-presents the
// existing computeReadiness() result. No portal — a real page in the App
// view tree.

import React, { useEffect, useMemo, useState } from 'react';
import styles from './ReadinessPage.module.css';
import { useLearnerAuth } from '../../contexts/LearnerAuthContext';
import { getExamConfig, getActiveCertIds, getBaselineColumn } from '../../config/exams';
import LearnerDashboardEmpty from '../dashboard/LearnerDashboardEmpty';
import CertTrackSwitcher from '../certification/CertTrackSwitcher';
import ScreenHeader from '../shared/ScreenHeader';
import DiscordPromo from '../shared/DiscordPromo';
import {
  computeReadiness,
  loadSessionsForReadiness,
  loadReadinessSnapshots,
} from '../../services/readinessService';
import { computeWeeklyTrend } from '../../services/weeklyTrendService';
import { loadEarnedAchievements } from '../../services/achievementService';
import JourneyRoadmap from '../journey/JourneyRoadmap';
import {
  ReadinessHeroPanel, FactorStack, CoachingCard, OpportunityPanel,
} from './ReadinessSections';
import {
  FactorGuide, HistoryCard, ContributionTable, ReminderBanner,
} from './ReadinessExtras';

const ACTIVE_CERTS = getActiveCertIds();

export default function ReadinessPage({ onNavigate, onStartCert, onStartSession }) {
  const { profile } = useLearnerAuth();

  const [activeCert, setActiveCert] = useState(null);
  const [readiness, setReadiness]   = useState(null);
  const [sessions, setSessions]     = useState([]);
  const [snapshots, setSnapshots]   = useState([]);
  const [earnedByKey, setEarnedByKey] = useState({});
  const [loading, setLoading]       = useState(true);
  // "Set a <cert> baseline" context dialog, opened from the track switcher.
  const [baselineDialogOpen, setBaselineDialogOpen] = useState(false);

  const baselines = useMemo(() => {
    const out = {};
    for (const cert of ACTIVE_CERTS) {
      const col = getBaselineColumn(cert);
      out[cert] = profile?.[col] || null;
    }
    return out;
  }, [profile]);
  const certsWithBaseline = ACTIVE_CERTS.filter((c) => !!baselines[c]);
  const hasAnyBaseline    = certsWithBaseline.length > 0;

  useEffect(() => {
    if (!hasAnyBaseline) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      // Default to first cert with a baseline (UI can switch).
      const cert = activeCert || certsWithBaseline[0];
      const [list, snaps, earnedRows] = await Promise.all([
        loadSessionsForReadiness(cert, baselines[cert]),
        loadReadinessSnapshots(cert, 20),
        loadEarnedAchievements({ limit: 100 }),
      ]);
      if (cancelled) return;
      setSessions(list);
      setSnapshots(snaps);
      const byKey = {};
      for (const row of (earnedRows || [])) byKey[row.achievement_key] = row;
      setEarnedByKey(byKey);
      setReadiness(computeReadiness({ examId: cert, sessions: list, baselineAt: baselines[cert] }));
      setActiveCert(cert);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAnyBaseline, profile?.baseline_at_pmp, profile?.baseline_at_cpmai, activeCert]);

  const trend = useMemo(() => computeWeeklyTrend(snapshots), [snapshots]);

  const certCfg   = activeCert ? getExamConfig(activeCert) : null;
  const examLabel = certCfg?.displayName || (activeCert ? activeCert.toUpperCase() : 'Your');
  const targetPct = typeof certCfg?.pass_target_pct === 'number' ? certCfg.pass_target_pct : 75;

  // "Start Practice" launches a session for the cert track being viewed
  // (not always PMP); falls back to the cert picker so the CTA never
  // dead-ends.
  const startPractice = () => (onStartSession ? onStartSession(activeCert) : onNavigate?.('home'));
  const viewPlan      = () => onNavigate?.('plan');

  return (
    <div className={styles.page} data-testid="readiness-page">
      {/* ── Standard topmast (ScreenHeader banner) ── */}
      <ScreenHeader
        variant="banner"
        coachPose="idle"
        onBack={() => onNavigate?.('dashboard')}
        backTestId="readiness-back"
        eyebrow="Your Certification Journey"
        title={<>{examLabel}<br />Readiness</>}
        promo={<DiscordPromo />}
      />

      {!hasAnyBaseline ? (
        <p className={styles.empty} data-testid="readiness-empty">
          Take your first session to set a baseline — readiness calibrates
          from there. <button type="button" className={styles.inlineLink}
            onClick={() => onNavigate?.('home')}>Pick a cert ›</button>
        </p>
      ) : loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : (
        <>
          {/* Both tracks always visible — switching (and starting the other
              cert) stays discoverable even with a single baseline. */}
          <CertTrackSwitcher
            activeCert={activeCert}
            baselines={baselines}
            onSelect={setActiveCert}
            onStartCert={() => setBaselineDialogOpen(true)}
            testIdPrefix="readiness"
          />

          <div className={styles.heroRow}>
            <ReadinessHeroPanel
              readiness={readiness}
              trend={trend}
              sessionCount={sessions.length}
              onStartPractice={startPractice}
              onViewPlan={viewPlan}
            />
            <FactorStack readiness={readiness} />
            <CoachingCard
              readiness={readiness}
              onStartPractice={startPractice}
              onViewPlan={viewPlan}
            />
          </div>

          <OpportunityPanel
            readiness={readiness}
            weakPhases={readiness?.weakPhases}
            onStartPractice={startPractice}
            onViewPlan={viewPlan}
          />

          <div className={styles.bandSpace}>
            <JourneyRoadmap
              readiness={readiness}
              sessions={sessions}
              baselineAt={activeCert ? baselines[activeCert] : null}
              earnedByKey={earnedByKey}
              onViewMilestones={() => onNavigate?.('milestones')}
            />
          </div>

          <FactorGuide readiness={readiness} />

          <ContributionTable readiness={readiness} />

          <HistoryCard snapshots={snapshots} trend={trend} targetPct={targetPct} />

          <ReminderBanner onStartPractice={startPractice} />
        </>
      )}

      {/* "Set a <cert> baseline" context dialog — opened from the track switcher
          when the learner picks a cert they haven't started yet. */}
      <LearnerDashboardEmpty
        isOpen={baselineDialogOpen}
        onClose={() => setBaselineDialogOpen(false)}
        onStartCert={onStartCert}
        baselines={baselines}
        displayName={profile?.display_name || null}
      />
    </div>
  );
}
